import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { Reveal, scrollToId } from "@/components/Reveal";
import { SERVICES, CATEGORIES } from "@/data/services";

const SPANS = [
  "lg:col-span-4",
  "lg:col-span-2",
  "lg:col-span-2",
  "lg:col-span-2",
  "lg:col-span-2",
  "lg:col-span-3",
  "lg:col-span-3",
  "lg:col-span-3",
  "lg:col-span-3",
];

const ServiceDescription = ({ service }) => {
  if (service.id !== "tax-prep") return <>{service.description}</>;
  const [before, after] = service.description.split("TrueFile Tax Co");
  return (
    <>
      {before}
      <a
        href="https://truefiletaxco.com/"
        target="_blank"
        rel="noopener noreferrer"
        data-testid="truefile-partner-link"
        className="font-medium text-gold underline decoration-gold/40 underline-offset-2 transition-colors duration-300 hover:text-gold-light"
      >
        TrueFile Tax Co
      </a>
      {after}
    </>
  );
};

export const Services = () => {
  const [active, setActive] = useState("All");
  const visible = SERVICES.filter((s) => active === "All" || s.category === active);

  return (
    <section id="services" data-testid="services-section" className="relative bg-obsidian py-28 lg:py-40">
      <div className="mx-auto max-w-7xl px-6 lg:px-10">
        <Reveal>
          <div className="flex flex-col gap-8 lg:flex-row lg:items-end lg:justify-between">
            <div>
              <p className="eyebrow mb-6" data-testid="services-eyebrow">Services Provided</p>
              <h2 className="font-display text-2xl font-semibold tracking-tight text-white sm:text-3xl lg:text-4xl">
                Nine instruments.
                <br />
                <span className="text-gold-gradient">One architecture.</span>
              </h2>
            </div>
            <div className="flex flex-wrap gap-2" data-testid="services-filter-tabs">
              {CATEGORIES.map((c) => (
                <button
                  key={c}
                  onClick={() => setActive(c)}
                  data-testid={`filter-tab-${c.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                  className={`border px-4 py-2 font-mono text-[10px] uppercase tracking-[0.2em] transition-all duration-300 ${
                    active === c
                      ? "border-gold bg-gold/15 text-gold"
                      : "border-white/15 text-white/50 hover:border-gold/40 hover:text-gold"
                  }`}
                >
                  {c}
                </button>
              ))}
            </div>
          </div>
        </Reveal>

        <motion.div layout className="mt-16 grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-6">
          <AnimatePresence mode="popLayout">
            {visible.map((s, i) => {
              const Icon = s.icon;
              return (
                <motion.article
                  layout
                  key={s.id}
                  initial={{ opacity: 0, y: 24 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.97 }}
                  transition={{ duration: 0.5, delay: i * 0.04, ease: [0.22, 1, 0.36, 1] }}
                  className={`gold-border-glow group relative flex flex-col justify-between bg-wine/80 p-8 ${SPANS[i % SPANS.length]}`}
                  data-testid={`service-card-${s.id}`}
                >
                  <div>
                    <div className="flex items-start justify-between">
                      <span className="flex h-11 w-11 items-center justify-center border border-gold/30 bg-royal/60 text-gold transition-colors duration-500 group-hover:bg-gold group-hover:text-obsidian">
                        <Icon size={18} strokeWidth={1.5} />
                      </span>
                      <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/35">
                        {String(SERVICES.indexOf(s) + 1).padStart(2, "0")} · {s.tag}
                      </span>
                    </div>
                    <h3 className="mt-6 font-display text-xl font-medium text-white sm:text-2xl">
                      {s.title}
                    </h3>
                    <p className="mt-3 text-sm leading-relaxed text-white/55"><ServiceDescription service={s} /></p>
                  </div>
                  <div className="mt-8 flex items-center justify-between border-t border-gold/10 pt-5">
                    <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-gold/80">
                      {s.highlight}
                    </span>
                    <button
                      onClick={() => scrollToId("#contact")}
                      data-testid={`service-request-${s.id}`}
                      className="flex items-center gap-1 font-mono text-[10px] uppercase tracking-[0.2em] text-white/40 transition-colors duration-300 hover:text-gold"
                    >
                      Request <ArrowUpRight size={13} />
                    </button>
                  </div>
                </motion.article>
              );
            })}
          </AnimatePresence>
        </motion.div>
      </div>
    </section>
  );
};
